import {
  Box,
  Divider,
  FormControl,
  FormLabel,
  Input,
  Image,
  Link,
  Stack,
  StackProps,
  useBreakpointValue,
  useDisclosure,
} from "@chakra-ui/react";
import { ArrowForwardIcon } from "@chakra-ui/icons";
import CDS from "design-system";
import Logo from "assets/camino_logo_transparent.svg";
import apiClient from "services/ApiClient";
import { isNetworkingError } from "services/ApiClient/networkingError";
import { useState, useMemo, useEffect } from "react";
import { API_ROUTES } from "definitions/constants/routeConstants";
import SignInOptionsForm from "components/Forms/SignInOptionsForm";
import useModal from "hooks/useModal";
import Alert from "../Alert/Alert";

const SUCCESS_ALERT_STATE = {
  title: "Check your inbox",
  body: (
    <CDS.Text14>
      If that email matches an account, we&apos;ve sent you a link to sign in. Check your spam folder too. If you
      don&apos;t see anything, reach out to your contact at the company you&apos;re joining.
    </CDS.Text14>
  ),
  buttonTitle: "Got it!",
};

// Map error codes from OAuth/SSO redirects to user-friendly messages
const ERROR_MESSAGES: Record<string, { title: string; body: React.ReactNode }> = {
  sso_required: {
    title: "SSO Login Required",
    body: (
      <>
        <CDS.Text14 mb="2">Your organization requires you to log in using Single Sign-On (SSO).</CDS.Text14>
        <CDS.Text14>Please contact your IT administrator or reach out to support@joincamino.com for assistance.</CDS.Text14>
      </>
    ),
  },
  pre_start_auth_not_allowed: {
    title: "Login Not Available",
    body: (
      <>
        <CDS.Text14 mb="2">Personal email login is only available before your start date.</CDS.Text14>
        <CDS.Text14>Please use your work email, or reach out to your contact at the company you&apos;re joining.</CDS.Text14>
      </>
    ),
  },
  login_conflict: {
    title: "Login Not Available",
    body: (
      <>
        <CDS.Text14 mb="2">We couldn&apos;t match your login to a single account.</CDS.Text14>
        <CDS.Text14>Please reach out to the contact at your company explaining you are having login issues.</CDS.Text14>
      </>
    ),
  },
  company_not_found: {
    title: "Company Not Found",
    body: (
      <>
        <CDS.Text14 mb="2">We couldn&apos;t find a Camino company associated with your account.</CDS.Text14>
        <CDS.Text14>If you think this is an error, please reach out to support@joincamino.com.</CDS.Text14>
      </>
    ),
  },
  user_not_found: {
    title: "Account Not Found",
    body: (
      <>
        <CDS.Text14 mb="2">We couldn&apos;t find an account associated with your email.</CDS.Text14>
        <CDS.Text14>Please contact your HR team or reach out to support@joincamino.com for help.</CDS.Text14>
      </>
    ),
  },
  saml_error: {
    title: "SSO Login Failed",
    body: (
      <>
        <CDS.Text14 mb="2">There was a problem with your SSO login.</CDS.Text14>
        <CDS.Text14>Please try again or contact your IT administrator for assistance.</CDS.Text14>
      </>
    ),
  },
  saml_validation_failed: {
    title: "SSO Validation Failed",
    body: (
      <>
        <CDS.Text14 mb="2">The SSO response could not be validated.</CDS.Text14>
        <CDS.Text14>This may be a configuration issue. Please contact your IT administrator.</CDS.Text14>
      </>
    ),
  },
  oidc_error: {
    title: "SSO Login Failed",
    body: (
      <>
        <CDS.Text14 mb="2">There was a problem with your SSO login.</CDS.Text14>
        <CDS.Text14>Please try again or contact your IT administrator for assistance.</CDS.Text14>
      </>
    ),
  },
  invalid_token: {
    title: "Invalid Login Token",
    body: (
      <>
        <CDS.Text14 mb="2">The login token is invalid or has expired.</CDS.Text14>
        <CDS.Text14>Please try logging in again.</CDS.Text14>
      </>
    ),
  },
  provider_not_found: {
    title: "SSO Not Configured",
    body: (
      <>
        <CDS.Text14 mb="2">SSO is not configured for your organization.</CDS.Text14>
        <CDS.Text14>Please contact your IT administrator or use an alternative login method.</CDS.Text14>
      </>
    ),
  },
  authentication_failed: {
    title: "Authentication Failed",
    body: (
      <>
        <CDS.Text14 mb="2">We couldn&apos;t complete your login.</CDS.Text14>
        <CDS.Text14>Please try again or contact support@joincamino.com if the problem persists.</CDS.Text14>
      </>
    ),
  },
  oauth_error: {
    title: "Google Login Failed",
    body: (
      <>
        <CDS.Text14 mb="2">There was a problem signing in with Google.</CDS.Text14>
        <CDS.Text14>Please try again or use an alternative login method.</CDS.Text14>
      </>
    ),
  },
};

interface AuthenticationOptions {
  hasSSO: boolean;
  ssoEnforced: boolean;
  ssoUrl?: string;
  ssoProviderName?: string;
}

// Parse error from URL query params on initial load (OAuth/SSO redirects)
function getInitialErrorFromUrl() {
  const urlParams = new URLSearchParams(window.location.search);
  const errorCode = urlParams.get("error");
  if (errorCode && ERROR_MESSAGES[errorCode]) {
    urlParams.delete("error");
    const newUrl = urlParams.toString()
      ? `${window.location.pathname}?${urlParams.toString()}`
      : window.location.pathname;
    window.history.replaceState({}, "", newUrl);
    return ERROR_MESSAGES[errorCode];
  }
  return null;
}

interface SignInChoiceCardProps {
  title: string;
  subtitle: string;
  onClick: () => void;
}

function SignInChoiceCard({ title, subtitle, onClick }: SignInChoiceCardProps) {
  return (
    <CDS.Card
      as="button"
      type="button"
      onClick={onClick}
      interactive
      radius="large"
      borderColor="gray.300"
      width="100%"
      textAlign="left"
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      gap="4"
    >
      <Box>
        <CDS.Text16 fontWeight="semibold" color="gray.900">
          {title}
        </CDS.Text16>
        <CDS.Text14 color="gray.600">{subtitle}</CDS.Text14>
      </Box>
      <ArrowForwardIcon boxSize="5" color="gray.500" />
    </CDS.Card>
  );
}

export default function SignInForm(props: StackProps) {
  const [initialError] = useState(getInitialErrorFromUrl);

  const { isOpen: isAlertOpen, onOpen: onAlertOpen, onClose: onAlertClose } = useDisclosure({
    defaultIsOpen: !!initialError,
  });
  const isMobile = useBreakpointValue({ base: true, md: false });
  const [email, setEmail] = useState<string>("");
  const [alertState, setAlertState] = useState(() =>
    initialError
      ? { title: initialError.title, body: initialError.body, buttonTitle: "OK" }
      : SUCCESS_ALERT_STATE,
  );
  const [isCheckingAuth, setIsCheckingAuth] = useState(false);
  const [authOptions, setAuthOptions] = useState<AuthenticationOptions | null>(null);
  const [bypassSSO, setBypassSSO] = useState(false);
  const [view, setView] = useState<"choose" | "work">("choose");

  const {
    open: openSignInModal,
    close: closeSignInModal,
    Modal: SignInModal,
  } = useModal({
    headerTitle: "Sign in",
    size: "md",
    isCentered: true,
  });

  // Check for iss query parameter on mount (IdP-initiated OIDC flow)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const issuer = urlParams.get("iss");

    if (issuer) {
      // Automatically fetch authentication options for the IdP (no email needed)
      const fetchAuthOptionsForIdP = async () => {
        setIsCheckingAuth(true);
        try {
          const response = await apiClient.post<AuthenticationOptions>(API_ROUTES.sessions.authenticationOptions, {
            iss: issuer,
          });
          setAuthOptions(response);
          setEmail(""); // Clear email since we're using IdP-initiated flow
          setBypassSSO(false);
          openSignInModal();

          // Clean up URL by removing iss parameter
          urlParams.delete("iss");
          const newUrl = urlParams.toString()
            ? `${window.location.pathname}?${urlParams.toString()}`
            : window.location.pathname;
          window.history.replaceState({}, "", newUrl);
        } catch (error) {
          const errorMessage =
            isNetworkingError(error) && error.errors.length > 0
              ? error.errors[0]
              : "There was a problem with SSO login. Please try again or contact your IT administrator.";

          setAlertState({
            title: "SSO Login Error",
            body: <CDS.Text14>{errorMessage}</CDS.Text14>,
            buttonTitle: "OK",
          });
          onAlertOpen();

          // Clean up URL on error
          urlParams.delete("iss");
          const newUrl = urlParams.toString()
            ? `${window.location.pathname}?${urlParams.toString()}`
            : window.location.pathname;
          window.history.replaceState({}, "", newUrl);
        } finally {
          setIsCheckingAuth(false);
        }
      };

      fetchAuthOptionsForIdP();
    }
  }, [onAlertOpen, openSignInModal]);

  const checkAuthenticationOptions = async () => {
    if (!email.trim()) return;

    setIsCheckingAuth(true);
    try {
      const response = await apiClient.post<AuthenticationOptions>(API_ROUTES.sessions.authenticationOptions, {
        email: email.trim(),
      });
      setAuthOptions(response);
      setBypassSSO(false);
      openSignInModal();
    } catch (error) {
      const errorMessage =
        isNetworkingError(error) && error.errors.length > 0
          ? error.errors[0]
          : "There was a problem checking your authentication options. Please try again.";

      setAlertState({
        title: "Error",
        body: <CDS.Text14>{errorMessage}</CDS.Text14>,
        buttonTitle: "OK",
      });
      onAlertOpen();
    } finally {
      setIsCheckingAuth(false);
    }
  };

  const handleNewHireBailout = () => {
    // Drop any work-view email so the personal path starts blank — SignInOptionsForm seeds its input from this.
    setEmail("");
    setBypassSSO(true);
    setAuthOptions({
      hasSSO: false,
      ssoEnforced: false,
    });
    openSignInModal();
  };

  const handleMagicLinkSent = () => {
    setAlertState(SUCCESS_ALERT_STATE);
    onAlertOpen();
  };

  const handleAlertClose = useMemo(
    () => () => {
      onAlertClose();
    },
    [onAlertClose],
  );

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && email.trim()) {
      checkAuthenticationOptions();
    }
  };

  return (
    <>
      <Stack spacing="6" {...props}>
        <Stack spacing="4">
          {isMobile && <Image mx={{ base: 6 }} maxWidth="160px" src={Logo} />}
          <CDS.Heading22>Sign in to Camino</CDS.Heading22>
        </Stack>

        {view === "choose" ? (
          <Stack spacing="4">
            <SignInChoiceCard
              title="I have my work email"
              subtitle="Sign in with your company email"
              onClick={() => setView("work")}
            />
            <SignInChoiceCard
              title="I'm starting soon"
              subtitle="Use the personal email your offer went to"
              onClick={handleNewHireBailout}
            />

            <Divider />
            <CDS.Text14 color="gray.600">
              Not sure which?{" "}
              <Link
                href="mailto:support@joincamino.com"
                color="brand.500"
                fontWeight="medium"
                _hover={{ textDecoration: "none" }}
              >
                Get help
              </Link>
            </CDS.Text14>
          </Stack>
        ) : (
          <Stack spacing="4">
            <FormControl
              position="relative"
              sx={{
                "&:focus-within .sign-in-label": {
                  transform: "scale(0.75) translateY(-10px)",
                },
                "input:not(:placeholder-shown) + .sign-in-label": {
                  transform: "scale(0.75) translateY(-10px)",
                },
              }}
            >
              <Input
                id="email"
                type="email"
                placeholder=" "
                value={email}
                onChange={(event) => setEmail(event.currentTarget.value)}
                onKeyPress={handleKeyPress}
                height="3rem"
                paddingTop="18px"
              />
              <FormLabel
                className="sign-in-label"
                htmlFor="email"
                position="absolute"
                top="14px"
                left="12px"
                color="gray.400"
                fontSize="md"
                pointerEvents="none"
                transition="all 0.2s"
                transformOrigin="left top"
                zIndex={2}
                m={0}
                pr={2}
              >
                Enter your work email
              </FormLabel>
            </FormControl>

            <CDS.Button
              type="submit"
              disabled={!email.trim()}
              loading={isCheckingAuth}
              onClick={checkAuthenticationOptions}
            >
              Continue
            </CDS.Button>

            <Link
              alignSelf="start"
              fontSize="sm"
              color="gray.600"
              onClick={() => setView("choose")}
              cursor="pointer"
              _hover={{ textDecoration: "none" }}
            >
              ← Back
            </Link>
          </Stack>
        )}
      </Stack>

      <Alert {...alertState} isOpen={isAlertOpen} onClose={handleAlertClose} />

      <SignInModal>
        {authOptions && (
          <>
            {email && (
              <CDS.Text12 fontWeight="normal" color="gray.600" mb={4}>
                {email}
              </CDS.Text12>
            )}
            <SignInOptionsForm
              email={email.trim()}
              ssoUrl={bypassSSO ? undefined : authOptions.ssoUrl}
              ssoProviderName={bypassSSO ? undefined : authOptions.ssoProviderName}
              ssoEnforced={bypassSSO ? false : authOptions.ssoEnforced}
              emailType={bypassSSO ? "personal" : "work"}
              onMagicLinkSent={handleMagicLinkSent}
              onClose={closeSignInModal}
            />
          </>
        )}
      </SignInModal>
    </>
  );
}
